import type { ReactNode } from "react";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { getAppBarVariant } from "~/components/app-bar/helpers";
import { getApplication } from "~/components/app-initializer/helpers";
import { checkIsPlusArticle } from "~/components/article/article-render/plus/helpers";
import { BaseEntityLink } from "~/components/base-entity-link";
import type { LinkEntity } from "~/components/base-entity-link/types";
import type { Props as BaseLinkProps } from "~/components/common/base-link";
import { checkIsStyleSectionContent } from "~/components/section/helpers";
import { Variant as StyleVariant } from "~/pages/section/enums";
import { getSectionRelatedSectionsQueueName } from "~/pages/section/helpers";
import type { entityLink$key } from "~/queries/__generated__/entityLink.graphql";

export type Props = {
  children?: ReactNode;
  reference: entityLink$key;
} & Omit<BaseLinkProps, "href" | "as" | "pathname">;

export const EntityLink = forwardRef<HTMLAnchorElement, Props>((props, reference) => {
  const { children, query, reference: reference_, ...remainingProps } = props;
  const linkableEntity = useFragment(
    graphql`
      fragment entityLink on BaseWithApplicationAndUrlAlias {
        __typename
        entityId
        entityUuid
        urlAlias
        urlRedirect {
          toUrl
        }
        application {
          entityId
        }
        ... on Section {
          fullSectionPath {
            entityId
          }
          parentSection
        }
        ...helpersAppBarVariantBase
        ...helpersApplicationBase
        ...helpersCheckIsPlusArticle
        ...helpersStyleSectionContent
      }
    `,
    reference_,
  );

  const {
    __typename,
    application,
    entityId,
    entityUuid,
    fullSectionPath = [],
    parentSection,
    urlAlias,
    urlRedirect,
  } = linkableEntity;

  const isPlus = checkIsPlusArticle(linkableEntity) || query?.display?.toLowerCase() === "plus";
  const linkEntity: LinkEntity = {
    applicationId: application?.entityId,
    entityId,
    parentSection,
    routeType: isPlus ? "Plus" : __typename,
    urlAlias,
    urlRedirectPath: urlRedirect?.toUrl,
  };

  const additionalQueryParameters = {
    appBarVariant: getAppBarVariant(linkableEntity),
    application: getApplication(linkableEntity),
    entityUuid,
    parentSection: parentSection ?? "",
    relatedSectionsQueueName:
      __typename === "Section"
        ? getSectionRelatedSectionsQueueName(fullSectionPath?.flatMap(s => s?.entityId ?? []) ?? [])
        : undefined,
    sectionVariant: checkIsStyleSectionContent(linkableEntity)
      ? StyleVariant.Style
      : StyleVariant.Default,
  };

  return (
    <BaseEntityLink
      linkEntity={linkEntity}
      {...remainingProps}
      query={{
        ...query,
        ...additionalQueryParameters,
      }}
      ref={reference}
    >
      {children}
    </BaseEntityLink>
  );
});

EntityLink.displayName = "EntityLink";
